import type { CSSProperties, ReactNode, RefObject } from 'react';
import { BTH_LOGO_CANDIDATES } from '../brand/BthLogo';
import type { DesignCatalogueDraftItem, DesignCoverBgMode } from '../../lib/designCatalogues';
import { chunkCatalogueItems, emptyDesignCatalogueItem } from '../../lib/designCatalogues';
import {
  designCatalogueAccentColor,
  designCatalogueLineColor,
} from '../../lib/designImagePalette';
import {
  designTemplateById,
  resolveDesignLayoutGrid,
  type DesignCatalogueTemplateDef,
} from '../../lib/designTemplates';

export type CataloguePreviewModel = {
  title: string;
  templateId: string;
  coverTitle: string;
  coverSubtitle: string;
  coverNotes: string;
  items: DesignCatalogueDraftItem[];
  layoutColumns?: number | null;
  layoutRows?: number | null;
  coverBgMode?: DesignCoverBgMode;
  coverBgColor?: string;
  coverBgColorTo?: string;
  coverBgImageUrl?: string | null;
};

type DesignCataloguePreviewProps = {
  model: CataloguePreviewModel;
  captureRef?: RefObject<HTMLDivElement | null>;
};

const BTH_LOGO_SRC = BTH_LOGO_CANDIDATES[0];
const DISCLAIMER = 'Product images are for illustration purpose only. 图片仅供参考';

function isTwoPartCover(layout: DesignCatalogueTemplateDef['coverLayout']): boolean {
  return layout === 'banner-top' || layout === 'split-panel' || layout === 'side-rail';
}

function buildCoverBgStyle(
  model: CataloguePreviewModel,
  layout?: DesignCatalogueTemplateDef['coverLayout'],
): CSSProperties | undefined {
  const mode = model.coverBgMode ?? 'theme';
  if (mode === 'color') {
    const from = (model.coverBgColor || '#9a3412').trim();
    const to = (model.coverBgColorTo || from).trim();
    // Two-part covers: From = top/accent panel only; To paints the lower panel separately.
    if (layout && isTwoPartCover(layout)) {
      return { background: from };
    }
    return {
      background: `linear-gradient(145deg, ${from} 0%, ${to} 100%)`,
    };
  }
  if (mode === 'image' && model.coverBgImageUrl) {
    return {
      backgroundImage: `linear-gradient(160deg, rgba(0,0,0,0.28), rgba(0,0,0,0.55)), url(${model.coverBgImageUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    };
  }
  return undefined;
}

function buildSecondaryPanelStyle(model: CataloguePreviewModel): CSSProperties | undefined {
  const mode = model.coverBgMode ?? 'theme';
  if (mode !== 'color' && mode !== 'image') return undefined;
  // Image mode: tint the lower panel from extracted "to"; cover primary still shows the photo.
  if (mode === 'image' && !(model.coverBgColorTo || model.coverBgColor || '').trim()) {
    return undefined;
  }
  const to = (model.coverBgColorTo || model.coverBgColor || '#fffbeb').trim();
  return { background: to };
}

function buildCustomColorVars(model: CataloguePreviewModel): CSSProperties | undefined {
  const mode = model.coverBgMode ?? 'theme';
  if (mode !== 'color' && mode !== 'image') return undefined;
  const from = (model.coverBgColor || '').trim();
  const to = (model.coverBgColorTo || from).trim();
  if (!from) return undefined;
  const panel = to || from;
  const line = designCatalogueLineColor(from, panel);
  const accent = designCatalogueAccentColor(from, panel);
  return {
    ['--design-cat-c1' as string]: from,
    ['--design-cat-c2' as string]: panel,
    ['--design-cat-line' as string]: line,
    ['--design-cat-accent' as string]: accent,
  };
}

function formatPrice(value: string): string {
  const t = value.trim();
  if (!t) return '';
  if (t.startsWith('$') || t.startsWith('S$')) return t;
  return `S$${t}`;
}

function Prices({ item }: { item: DesignCatalogueDraftItem }) {
  const promo = item.promo_price.trim();
  const price = item.price.trim();
  if (!promo && !price) return null;
  if (promo) {
    return (
      <div className="design-cat-card__prices">
        <span className="design-cat-card__promo">{formatPrice(promo)}</span>
        {price ? <span className="design-cat-card__was">{formatPrice(price)}</span> : null}
      </div>
    );
  }
  return (
    <div className="design-cat-card__prices">
      <span className="design-cat-card__promo">{formatPrice(price)}</span>
    </div>
  );
}

function ItemMeta({ item }: { item: DesignCatalogueDraftItem }) {
  return (
    <>
      {item.category.trim() ? (
        <p className="design-cat-card__category">{item.category.trim()}</p>
      ) : null}
      <h3 className="design-cat-card__name">{item.name.trim() || 'Untitled item'}</h3>
      {item.item_code.trim() ? <p className="design-cat-card__code">{item.item_code.trim()}</p> : null}
      {item.packing.trim() ? <p className="design-cat-card__pack">{item.packing.trim()}</p> : null}
      <Prices item={item} />
      {item.notes.trim() ? <p className="design-cat-card__notes">{item.notes.trim()}</p> : null}
    </>
  );
}

function RowMeta({ item }: { item: DesignCatalogueDraftItem }) {
  const category = item.category.trim();
  const code = item.item_code.trim();
  const packing = item.packing.trim();
  const notes = item.notes.trim();
  const hasPrice = Boolean(item.promo_price.trim() || item.price.trim());
  const hasSpecs = Boolean(code || packing || hasPrice);

  return (
    <div className="design-cat-row__main">
      {category ? <p className="design-cat-card__category">{category}</p> : null}
      <h3 className="design-cat-card__name">{item.name.trim() || 'Untitled item'}</h3>
      {hasSpecs ? (
        <div className="design-cat-row__specs">
          {code ? (
            <div className="design-cat-row__spec">
              <span className="design-cat-row__spec-label">Item code</span>
              <span className="design-cat-row__spec-value">{code}</span>
            </div>
          ) : null}
          {packing ? (
            <div className="design-cat-row__spec">
              <span className="design-cat-row__spec-label">Packing</span>
              <span className="design-cat-row__spec-value">{packing}</span>
            </div>
          ) : null}
          {hasPrice ? (
            <div className="design-cat-row__spec design-cat-row__spec--price">
              <span className="design-cat-row__spec-label">Price</span>
              <Prices item={item} />
            </div>
          ) : null}
        </div>
      ) : null}
      {notes ? <p className="design-cat-card__notes">{notes}</p> : null}
    </div>
  );
}

function Media({
  item,
  className = 'design-cat-card__media',
}: {
  item: DesignCatalogueDraftItem;
  className?: string;
}) {
  return (
    <div className={className}>
      {item.image_url ? (
        <img src={item.image_url} alt={item.name || 'Product'} />
      ) : (
        <div className="design-cat-card__placeholder">No photo</div>
      )}
      {item.badge.trim() ? <span className="design-cat-card__badge">{item.badge.trim()}</span> : null}
    </div>
  );
}

function CoverPage({
  template,
  coverTitle,
  coverSubtitle,
  coverNotes,
  bgStyle,
  secondaryStyle,
}: {
  template: DesignCatalogueTemplateDef;
  coverTitle: string;
  coverSubtitle: string;
  coverNotes: string;
  bgStyle?: CSSProperties;
  secondaryStyle?: CSSProperties;
}) {
  const layout = template.coverLayout;

  if (layout === 'split-panel') {
    return (
      <section className="design-cat-page design-cat-page--cover design-cat-page--cover-split">
        <div className="design-cat-cover-split__text" style={secondaryStyle}>
          <img className="design-cat-page__logo" src={BTH_LOGO_SRC} alt="Ban Teck Han" />
          <p className="design-cat-page__eyebrow">Ban Teck Han · Catalogue</p>
          <h2 className="design-cat-page__cover-title">{coverTitle}</h2>
          <p className="design-cat-page__cover-sub">{coverSubtitle}</p>
          {coverNotes ? <p className="design-cat-page__cover-notes">{coverNotes}</p> : null}
          <p className="design-cat-page__disclaimer">{DISCLAIMER}</p>
        </div>
        <div className="design-cat-cover-split__panel" style={bgStyle} aria-hidden />
      </section>
    );
  }

  if (layout === 'center-hero') {
    return (
      <section className="design-cat-page design-cat-page--cover">
        <div className="design-cat-page__bleed design-cat-page__bleed--center" style={bgStyle}>
          <img className="design-cat-page__logo" src={BTH_LOGO_SRC} alt="Ban Teck Han" />
          <p className="design-cat-page__eyebrow">Ban Teck Han · Catalogue</p>
          <h2 className="design-cat-page__cover-title">{coverTitle}</h2>
          <p className="design-cat-page__cover-sub">{coverSubtitle}</p>
          {coverNotes ? <p className="design-cat-page__cover-notes">{coverNotes}</p> : null}
          <p className="design-cat-page__disclaimer">{DISCLAIMER}</p>
        </div>
      </section>
    );
  }

  if (layout === 'banner-top') {
    return (
      <section className="design-cat-page design-cat-page--cover design-cat-page--cover-banner">
        <div className="design-cat-cover-banner__band" style={bgStyle}>
          <img className="design-cat-page__logo design-cat-page__logo--on-dark" src={BTH_LOGO_SRC} alt="Ban Teck Han" />
          <p className="design-cat-page__eyebrow">Ban Teck Han · Catalogue</p>
          <h2 className="design-cat-page__cover-title">{coverTitle}</h2>
          <p className="design-cat-page__cover-sub">{coverSubtitle}</p>
        </div>
        <div className="design-cat-cover-banner__body" style={secondaryStyle}>
          {coverNotes ? <p className="design-cat-page__cover-notes">{coverNotes}</p> : (
            <p className="design-cat-cover-banner__lead">Limited-time offers — while stocks last.</p>
          )}
          <p className="design-cat-page__disclaimer design-cat-page__disclaimer--dark">{DISCLAIMER}</p>
        </div>
      </section>
    );
  }

  if (layout === 'side-rail') {
    return (
      <section className="design-cat-page design-cat-page--cover design-cat-page--cover-rail">
        <aside className="design-cat-cover-rail__aside" style={bgStyle}>
          <span className="design-cat-cover-rail__vert">Catalogue</span>
        </aside>
        <div className="design-cat-cover-rail__main" style={secondaryStyle}>
          <div className="design-cat-cover-rail__brand">
            <img className="design-cat-page__logo" src={BTH_LOGO_SRC} alt="Ban Teck Han" />
            <p className="design-cat-page__eyebrow">Ban Teck Han</p>
          </div>
          <h2 className="design-cat-page__cover-title">{coverTitle}</h2>
          <p className="design-cat-page__cover-sub">{coverSubtitle}</p>
          {coverNotes ? <p className="design-cat-page__cover-notes">{coverNotes}</p> : null}
          <p className="design-cat-page__disclaimer">{DISCLAIMER}</p>
        </div>
      </section>
    );
  }

  if (layout === 'stamp') {
    return (
      <section className="design-cat-page design-cat-page--cover">
        <div className="design-cat-page__bleed design-cat-page__bleed--stamp" style={bgStyle}>
          <img className="design-cat-page__logo" src={BTH_LOGO_SRC} alt="Ban Teck Han" />
          <div className="design-cat-stamp">
            <span className="design-cat-stamp__label">FREE</span>
            <span className="design-cat-stamp__sub">GIFT</span>
          </div>
          <h2 className="design-cat-page__cover-title">{coverTitle}</h2>
          <p className="design-cat-page__cover-sub">{coverSubtitle}</p>
          {coverNotes ? <p className="design-cat-page__cover-notes">{coverNotes}</p> : null}
          <p className="design-cat-page__disclaimer">{DISCLAIMER}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="design-cat-page design-cat-page--cover">
      <div className="design-cat-page__bleed" style={bgStyle}>
        <div className="design-cat-page__brand">
          <img className="design-cat-page__logo" src={BTH_LOGO_SRC} alt="Ban Teck Han" />
          <p className="design-cat-page__eyebrow">Ban Teck Han · Catalogue</p>
        </div>
        <h2 className="design-cat-page__cover-title">{coverTitle}</h2>
        <p className="design-cat-page__cover-sub">{coverSubtitle}</p>
        {coverNotes ? <p className="design-cat-page__cover-notes">{coverNotes}</p> : null}
        <p className="design-cat-page__disclaimer">{DISCLAIMER}</p>
      </div>
    </section>
  );
}

function PageChrome({
  template,
  columns,
  rows,
  coverTitle,
  pageLabel,
  children,
}: {
  template: DesignCatalogueTemplateDef;
  columns: 1 | 2 | 3;
  rows: number;
  coverTitle: string;
  pageLabel: string;
  children: ReactNode;
}) {
  const chrome = template.pageChrome;
  const gridVars = {
    ['--design-cat-cols' as string]: columns,
    ['--design-cat-rows' as string]: rows,
    ['--design-cat-nested-rows' as string]: Math.max(1, rows - 1),
  } as CSSProperties;

  if (chrome === 'colour-band') {
    return (
      <section
        className={`design-cat-page design-cat-page--grid design-cat-page--chrome-band design-cat-page--cols-${columns}`}
        style={gridVars}
      >
        <header className="design-cat-page__band">
          <span className="design-cat-page__band-brand">
            <img className="design-cat-page__logo design-cat-page__logo--sm design-cat-page__logo--on-dark" src={BTH_LOGO_SRC} alt="" />
            <span>{coverTitle}</span>
          </span>
          <span>{pageLabel}</span>
        </header>
        {children}
        <footer className="design-cat-page__foot">{DISCLAIMER}</footer>
      </section>
    );
  }

  if (chrome === 'side-rail') {
    return (
      <section
        className={`design-cat-page design-cat-page--grid design-cat-page--chrome-rail design-cat-page--cols-${columns}`}
        style={gridVars}
      >
        <aside className="design-cat-page__rail">
          <span className="design-cat-page__rail-text">{coverTitle}</span>
          <span className="design-cat-page__rail-page">{pageLabel}</span>
        </aside>
        <div className="design-cat-page__rail-body">
          <header className="design-cat-page__rail-head">
            <img className="design-cat-page__logo design-cat-page__logo--sm" src={BTH_LOGO_SRC} alt="" />
          </header>
          {children}
          <footer className="design-cat-page__foot">{DISCLAIMER}</footer>
        </div>
      </section>
    );
  }

  if (chrome === 'minimal') {
    return (
      <section
        className={`design-cat-page design-cat-page--grid design-cat-page--chrome-minimal design-cat-page--cols-${columns}`}
        style={gridVars}
      >
        <header className="design-cat-page__minimal">
          <span className="design-cat-page__bar-brand">
            <img className="design-cat-page__logo design-cat-page__logo--sm" src={BTH_LOGO_SRC} alt="" />
            <span>{coverTitle}</span>
          </span>
          <span>{pageLabel}</span>
        </header>
        {children}
        <footer className="design-cat-page__foot">{DISCLAIMER}</footer>
      </section>
    );
  }

  if (chrome === 'footer-only') {
    return (
      <section
        className={`design-cat-page design-cat-page--grid design-cat-page--chrome-footer design-cat-page--cols-${columns}`}
        style={gridVars}
      >
        <header className="design-cat-page__minimal design-cat-page__minimal--soft">
          <strong>{coverTitle}</strong>
          <span>{pageLabel}</span>
        </header>
        {children}
        <footer className="design-cat-page__foot design-cat-page__foot--brand">
          <img className="design-cat-page__logo design-cat-page__logo--sm" src={BTH_LOGO_SRC} alt="" />
          <span>{DISCLAIMER}</span>
        </footer>
      </section>
    );
  }

  return (
    <section className={`design-cat-page design-cat-page--grid design-cat-page--cols-${columns}`} style={gridVars}>
      <header className="design-cat-page__bar">
        <span className="design-cat-page__bar-brand">
          <img className="design-cat-page__logo design-cat-page__logo--sm" src={BTH_LOGO_SRC} alt="" />
          <span>{coverTitle}</span>
        </span>
        <span>{pageLabel}</span>
      </header>
      {children}
      <footer className="design-cat-page__foot">{DISCLAIMER}</footer>
    </section>
  );
}

function padPageItems(
  items: DesignCatalogueDraftItem[],
  capacity: number,
): DesignCatalogueDraftItem[] {
  if (items.length >= capacity) return items.slice(0, capacity);
  const padded = [...items];
  while (padded.length < capacity) {
    padded.push(emptyDesignCatalogueItem(padded.length));
  }
  return padded;
}

function ProductCards({
  template,
  items,
  capacity,
}: {
  template: DesignCatalogueTemplateDef;
  items: DesignCatalogueDraftItem[];
  capacity: number;
}) {
  const pageItems = padPageItems(items, capacity);

  if (template.cardLayout === 'photo-left') {
    return (
      <div className="design-cat-page__list">
        {pageItems.map((item, index) => (
          <article key={index} className="design-cat-row">
            <Media item={item} className="design-cat-row__media" />
            <div className="design-cat-row__body">
              <RowMeta item={item} />
            </div>
          </article>
        ))}
      </div>
    );
  }

  if (template.cardLayout === 'ticket') {
    return (
      <div className="design-cat-page__tickets">
        {pageItems.map((item, index) => (
          <article key={index} className="design-cat-ticket">
            <Media item={item} className="design-cat-ticket__media" />
            <div className="design-cat-ticket__body">
              <ItemMeta item={item} />
            </div>
            <div className="design-cat-ticket__stub" aria-hidden>
              <span>BTH</span>
            </div>
          </article>
        ))}
      </div>
    );
  }

  if (template.cardLayout === 'feature-mix') {
    const [hero, ...rest] = pageItems;
    return (
      <div className="design-cat-page__feature-mix">
        <article className="design-cat-card design-cat-card--hero">
          <Media item={hero!} />
          <div className="design-cat-card__body">
            <ItemMeta item={hero!} />
          </div>
        </article>
        {rest.length > 0 ? (
          <div className="design-cat-page__grid design-cat-page__grid--nested">
            {rest.map((item, index) => (
              <article key={index} className="design-cat-card">
                <Media item={item} />
                <div className="design-cat-card__body">
                  <ItemMeta item={item} />
                </div>
              </article>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="design-cat-page__grid">
      {pageItems.map((item, index) => (
        <article key={index} className="design-cat-card">
          <Media item={item} />
          <div className="design-cat-card__body">
            <ItemMeta item={item} />
          </div>
        </article>
      ))}
    </div>
  );
}

export function DesignCataloguePreview({ model, captureRef }: DesignCataloguePreviewProps) {
  const template = designTemplateById(model.templateId);
  const grid = resolveDesignLayoutGrid(template, {
    columns: model.layoutColumns,
    rows: model.layoutRows,
  });
  const pages = chunkCatalogueItems(model.items, grid.itemsPerPage);
  const coverTitle = model.coverTitle.trim() || model.title.trim() || 'Promo Catalogue';
  const coverSubtitle = model.coverSubtitle.trim() || 'Special Offers';
  const coverNotes = model.coverNotes.trim();
  const themeClass = `design-cat-preview--theme-${template.theme}`;
  const layoutClass = `design-cat-preview--layout-${template.coverLayout}`;
  const coverBgStyle = buildCoverBgStyle(model, template.coverLayout);
  const secondaryPanelStyle = buildSecondaryPanelStyle(model);
  const customColorVars = buildCustomColorVars(model);
  const customColorClass = customColorVars ? ' design-cat-preview--custom-colors' : '';

  return (
    <div
      ref={captureRef}
      className={`design-cat-preview ${themeClass} ${layoutClass}${customColorClass}`}
      style={customColorVars}
    >
      <CoverPage
        template={template}
        coverTitle={coverTitle}
        coverSubtitle={coverSubtitle}
        coverNotes={coverNotes}
        bgStyle={coverBgStyle}
        secondaryStyle={secondaryPanelStyle}
      />

      {pages.map((pageItems, pageIndex) => (
        <PageChrome
          key={`page-${pageIndex}`}
          template={template}
          columns={grid.columns}
          rows={grid.rows}
          coverTitle={coverTitle}
          pageLabel={`Page ${pageIndex + 2} / ${pages.length + 1}`}
        >
          <ProductCards template={template} items={pageItems} capacity={grid.itemsPerPage} />
        </PageChrome>
      ))}
    </div>
  );
}
